import React, {useState} from 'react';
import {
  Box,
  Typography,
  TextField,
  Chip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {useSaveProductionComponents} from '../../../hooks/productions/useProductions';
import {SaveProductionComponentsPayload} from '../../../api/types/production.type';
import ArrButton from '../../../components/common/arr-button/ArrButton';
import ArrToast from '../../../components/common/arr-toast/ArrToast';
import {ReactComponent as CloseToast} from '../../../assets/images/close-toast.svg';
import {TOAST_DURATION} from '../../../shared/constants';

const SaveProductionComponentsApiTest: React.FC = () => {
  // Request parameters
  const [productionId, setProductionId] = useState<number>(30);
  const [securityGroupId, setSecurityGroupId] = useState<number>(1);
  const [moduleType, setModuleType] = useState<string>('Production');
  const [isMarkComplete, setIsMarkComplete] = useState<boolean>(true);

  // Component selections
  const [isDATSelected, setIsDATSelected] = useState<boolean>(true);
  const [isNativeSelected, setIsNativeSelected] = useState<boolean>(false);
  const [isTIFFSelected, setIsTIFFSelected] = useState<boolean>(false);
  const [isTextSelected, setIsTextSelected] = useState<boolean>(false);
  const [isPDFSelected, setIsPDFSelected] = useState<boolean>(false);
  const [isArchiveSelected, setIsArchiveSelected] = useState<boolean>(false);
  const [isTranslation, setIsTranslation] = useState<boolean>(false);
  const [isMP3FileSelected, setIsMP3FileSelected] = useState<boolean>(true);

  // Response state
  const [showToast, setShowToast] = useState<boolean>(false);
  const [copySuccess, setCopySuccess] = useState<boolean>(false);

  const {
    data: saveResponse,
    mutate: saveProductionComponents,
    error: saveError,
    isPending,
  } = useSaveProductionComponents(
    (data) => {
      setShowToast(true);
      console.log('Save successful:', data);
    },
    (error) => {
      setShowToast(true);
      console.error('Save failed:', error);
    }
  );

  const handleSave = () => {
    const payload: SaveProductionComponentsPayload = {
      moduleType,
      isMarkComplete,
      isDATSelected,
      isNativeSelected,
      isTIFFSelected,
      isTextSelected,
      isPDFSelected,
      isArchiveSelected,
      isTranslation,
      isMP3FileSelected,
      productionId,
      productionSecurityGroupId: securityGroupId,
      // Add component data only for selected components
      ...(isDATSelected && {
        datComponentModelData: {
          ansiFormatType: '1256',
          fieldSeparator: -1,
          textQualifier: -3,
          newLineSeparator: -1050,
          multiValue: -324,
          dateFormatID: 11,
          dateFormat: 'YYYY/MM/DD HH:MI:SS',
          datFieldMappingDataList: [
            {
              selectedTypeId: 'FIELDS',
              selectedSourceId: '-146',
              datField: 'ProdBeginningBatesBuddy',
              offSet: '',
              isPrivileged: false,
              isRedacted: false,
              sequenceNo: 0,
            },
          ],
          overwriteMasterDate: false,
          isAnsi: true,
          eTextFormat: 1,
          prependCarriageReturn: false,
        },
      }),
      ...(isNativeSelected && {
        nativeComponentModelData: {
          isSelectAllFileTypes: false,
          produceLoadFile: false,
          generateHTMLforEmails: false,
          produceNativeForTechDocs: false,
          producedLockedFiles: false,
          protectionOnExcelFile: false,
          nativeComponentmodel: '',
          nativeSelectedFileGroups: '257,258',
          nativeSelectedTags: '3,4',
          eRedactedAndPrevilededFamily: 0,
        },
      }),
      ...(isTextSelected && {
        textComponentData: {
          eLevelType: 0,
          eTextFormat: 1,
          breaksToMatchTIFFs: false,
          produceLoadFile: true,
          shouldSkipOCR: false,
          selectedANSIType: '',
          shouldExportLargeTextFiles: true,
        },
      }),
      ...(isMP3FileSelected && {
        mP3ComponentModelData: {
          produceLoadFile: true,
          redactionType: 1,
          isBurnRedactionSelected: true,
          isRedactionByAnnotation: false,
          specifiedRedactionTagList: '1,2,3,-1',
          fillerAudioID: '2',
          fillerAudioList: [],
        },
      }),
      ...(isTranslation && {
        translationComponentModelData: {
          produceLoadFile: true,
          isSelectAllFileTypes: false,
          translationSelectedFileGroups: '257,258',
          isTIFFSlipSheetEnabled: false,
          eRedactedAndPrevilededFamily: 0,
          shouldExcludeNativeOfParent: true,
        },
      }),
    };

    saveProductionComponents(payload);
  };

  const handleCopyPayload = async () => {
    const payload: SaveProductionComponentsPayload = {
      moduleType,
      isMarkComplete,
      isDATSelected,
      isNativeSelected,
      isTIFFSelected,
      isTextSelected,
      isPDFSelected,
      isArchiveSelected,
      isTranslation,
      isMP3FileSelected,
      productionId,
      productionSecurityGroupId: securityGroupId,
    };

    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <Box className="demo-page" padding={3}>
      <Typography
        variant="h4"
        className="typo-text headline2_size"
        gutterBottom
      >
        🧪 Save Production Components API Test
      </Typography>
      <Typography
        className="typo-text body3_size"
        sx={{color: '#666', marginBottom: 2}}
      >
        POST request to save production component configuration
      </Typography>
      {/* Basic Parameters */}
      <Box
        sx={{
          backgroundColor: '#f5f5f5',
          padding: 2,
          borderRadius: 1,
          marginBottom: 2,
        }}
      >
        <Typography
          variant="h6"
          className="typo-text subhead2_size"
          gutterBottom
        >
          Basic Parameters
        </Typography>

        <Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap'}}>
          <TextField
            label="Production ID"
            type="number"
            value={productionId}
            onChange={(e) => setProductionId(Number(e.target.value))}
            size="small"
          />
          <TextField
            label="Security Group ID"
            type="number"
            value={securityGroupId}
            onChange={(e) => setSecurityGroupId(Number(e.target.value))}
            size="small"
          />
          <TextField
            label="Module Type"
            type="text"
            value={moduleType}
            onChange={(e) => setModuleType(e.target.value)}
            size="small"
            placeholder="Production"
          />
        </Box>

        <Box sx={{marginTop: 2}}>
          <FormControlLabel
          data-testid="mark-complete-form-control-label-save-production-component"
            control={
              <Switch
                checked={isMarkComplete}
                onChange={(e) => setIsMarkComplete(e.target.checked)}
                color="primary"
              />
            }
            label="Mark Complete"
          />
        </Box>
      </Box>
      {/* Component Selection */}
      <Box
        sx={{
          backgroundColor: '#e8f5e9',
          padding: 2,
          borderRadius: 1,
          marginBottom: 2,
          border: '2px solid #4caf50',
        }}
      >
        <Typography
          variant="h6"
          className="typo-text subhead2_size"
          gutterBottom
          sx={{color: '#1b1b1b'}}
        >
          Select Components to Include
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 2,
            padding: 2,
            backgroundColor: '#fff',
            borderRadius: 1,
          }}
        >
          <FormControlLabel
          data-testid="dat-component-form-control-label-save-production-component"
            control={
              <Switch
                checked={isDATSelected}
                onChange={(e) => setIsDATSelected(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography
                className="typo-text body2_size"
                sx={{color: '#1b1b1b'}}
              >
                DAT Component
              </Typography>
            }
          />
          <FormControlLabel
          data-testid="native-component-form-control-label-save-production-component"
            control={
              <Switch
                checked={isNativeSelected}
                onChange={(e) => setIsNativeSelected(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography
                className="typo-text body2_size"
                sx={{color: '#1b1b1b'}}
              >
                Native Component
              </Typography>
            }
          />
          <FormControlLabel
          data-testid="tiff-component-form-control-label-save-production-component"
            control={
              <Switch
                checked={isTIFFSelected}
                onChange={(e) => setIsTIFFSelected(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography
                className="typo-text body2_size"
                sx={{color: '#1b1b1b'}}
              >
                TIFF Component
              </Typography>
            }
          />
          <FormControlLabel
          data-testid="text-component-form-control-label-save-production-component"
            control={
              <Switch
                checked={isTextSelected}
                onChange={(e) => setIsTextSelected(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography
                className="typo-text body2_size"
                sx={{color: '#1b1b1b'}}
              >
                Text Component
              </Typography>
            }
          />
          <FormControlLabel
          data-testid="pdf-component-form-control-label-save-production-component"
            control={
              <Switch
                checked={isPDFSelected}
                onChange={(e) => setIsPDFSelected(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography
                className="typo-text body2_size"
                sx={{color: '#1b1b1b'}}
              >
                PDF Component
              </Typography>
            }
          />
          <FormControlLabel
          data-testid="archive-component-form-control-label-save-production-component"
            control={
              <Switch
                checked={isArchiveSelected}
                onChange={(e) => setIsArchiveSelected(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography
                className="typo-text body2_size"
                sx={{color: '#1b1b1b'}}
              >
                Archive Component
              </Typography>
            }
          />
          <FormControlLabel
          data-testid="translation-component-form-control-label-save-production-component"
            control={
              <Switch
                checked={isTranslation}
                onChange={(e) => setIsTranslation(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography
                className="typo-text body2_size"
                sx={{color: '#1b1b1b'}}
              >
                Translation Component
              </Typography>
            }
          />
          <FormControlLabel
          data-testid="mp3-component-form-control-label-save-production-component"
            control={
              <Switch
                checked={isMP3FileSelected}
                onChange={(e) => setIsMP3FileSelected(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography
                className="typo-text body2_size"
                sx={{color: '#1b1b1b'}}
              >
                MP3 Component
              </Typography>
            }
          />
        </Box>
      </Box>
      {/* Action Buttons */}
      <Box sx={{display: 'flex', gap: 2, marginBottom: 3}}>
        <ArrButton
          variant="primary"
          size="large"
          onClick={handleSave}
          loading={isPending}
          dataTestId="save-production-components-api-test-save-production-components-button">
          💾 Save Production Components
        </ArrButton>
        <ArrButton
          variant={copySuccess ? 'secondary' : 'text'}
          size="large"
          onClick={handleCopyPayload}
          dataTestId="save-production-components-api-test-button">
          {copySuccess ? '✅ Copied!' : '📋 Copy Payload'}
        </ArrButton>
      </Box>
      {/* Success Response */}
      {saveResponse && !isPending && (
        <Box
          sx={{
            padding: 3,
            backgroundColor: '#e8f5e9',
            borderRadius: 1,
            border: '1px solid #4caf50',
            marginBottom: 2,
          }}
        >
          <Typography className="typo-text subhead3_size" color="success">
            ✅ Save Successful
          </Typography>
          <Box sx={{marginTop: 1}}>
            <Chip
              label={`Production ID: ${saveResponse.productionId}`}
              color="success"
              size="small"
              data-testid={`save-production-component-chip-production`}
            />
          </Box>
          <Typography className="typo-text body3_size" sx={{marginTop: 1}}>
            Message: {saveResponse.message}
          </Typography>
        </Box>
      )}
      {/* Error State */}
      {saveError && (
        <Box
          sx={{
            padding: 3,
            backgroundColor: '#ffebee',
            borderRadius: 1,
            border: '1px solid #f44336',
            marginBottom: 2,
          }}
        >
          <Typography className="typo-text subhead3_size" color="error">
            ❌ Save Failed
          </Typography>
          <Typography className="typo-text body3_size" color="error">
            {saveError.message}
          </Typography>
        </Box>
      )}
      {/* Loading State */}
      {isPending && (
        <Box
          sx={{
            padding: 3,
            backgroundColor: '#e3f2fd',
            borderRadius: 1,
            border: '1px solid #2196f3',
            marginBottom: 2,
          }}
        >
          <Typography className="typo-text body2_size">
            ⏳ Saving... Please wait...
          </Typography>
        </Box>
      )}
      {/* Request Payload Preview */}
      <Box
        sx={{
          marginTop: 2,
          padding: 2,
          backgroundColor: '#f5f5f5',
          borderRadius: 1,
        }}
      >
        <Typography
          variant="h6"
          className="typo-text subhead3_size"
          gutterBottom
        >
          📤 Request Payload Preview
        </Typography>
        <Box
          sx={{
            padding: 2,
            backgroundColor: '#263238',
            borderRadius: 1,
            overflow: 'auto',
            maxHeight: '400px',
          }}
        >
          <pre
            style={{
              color: '#4caf50',
              fontSize: '12px',
              margin: 0,
              fontFamily: 'monospace',
            }}
          >
            {JSON.stringify(
              {
                moduleType,
                isMarkComplete,
                isDATSelected,
                isNativeSelected,
                isTIFFSelected,
                isTextSelected,
                isPDFSelected,
                isArchiveSelected,
                isTranslation,
                isMP3FileSelected,
                productionId,
                productionSecurityGroupId: securityGroupId,
                ...(isDATSelected && {
                  datComponentModelData: {
                    ansiFormatType: '1256',
                    fieldSeparator: -1,
                    textQualifier: -3,
                    // ... sample data
                  },
                }),
                // Other components conditionally included
              },
              null,
              2
            )}
          </pre>
        </Box>
      </Box>
      {/* Network Details */}
      <Box sx={{marginTop: 4, padding: 2, backgroundColor: '#fafafa'}}>
        <Typography variant="h6" className="typo-text subhead3_size">
          🌐 Network Details
        </Typography>
        <Typography className="typo-text body3_size">
          <strong>Endpoint:</strong> /productions/api/v1/production/components
        </Typography>
        <Typography className="typo-text body3_size">
          <strong>Method:</strong> POST
        </Typography>
        <Typography className="typo-text body3_size">
          <strong>Content-Type:</strong> application/json
        </Typography>
        <Typography className="typo-text body3_size">
          <strong>Base URL:</strong>{' '}
          https://arr-api-productions-dev.us2kd2w30.k8d.consiliotest.com/
        </Typography>
      </Box>
      {/* Toast Notification */}
      {showToast && (
        <ArrToast
          id="save-production-components-toast"
          title={saveError ? 'Error' : 'Success'}
          message={
            saveError?.message || saveResponse?.message || 'Operation completed'
          }
          type={saveError ? 'error' : 'success'}
          duration={TOAST_DURATION}
          onClose={() => setShowToast(false)}
          dismissable={true}
          closeIcon={<CloseToast />}
          position="bottom-right"
        />
      )}
    </Box>
  );
};

export default SaveProductionComponentsApiTest;
